import _ from 'lodash';
import type { ReactNode } from 'react';
import type { FieldValues } from 'react-hook-form';
import type { Filter } from 'src/components/user-search-preferences/useGroupAndUserOptions';
import type { useGetUsers } from 'src/hooks/queries/user/useGetUsers';
import type { useGetUserGroups } from 'src/hooks/queries/user-group/useGetUserGroups';

import UserSearchPreferences from '../../user-search-preferences/UserSearchPreferences';
import ControlledMultiselect from '../controlled-multiselect';
import type { HidableOption } from '../controlled-multiselect/types';
import type { ControlledBaseProps } from '../types';
import styles from './style.module.scss';

type GetUsersData = NonNullable<ReturnType<typeof useGetUsers>['data']>;
type GetUserGroupsData = NonNullable<
  ReturnType<typeof useGetUserGroups>['data']
>;

type GetUserGroupsRow = NonNullable<
  ReturnType<typeof useGetUserGroups>['data']
>['user_group'][number];

interface Props<T extends FieldValues> extends ControlledBaseProps<T> {
  disabled?: boolean;
  constraintText?: ReactNode;
  testId?: string;
  includeGroups: boolean;
  userFilter?: Filter<GetUsersData['user'][number]>;
  groupFilter?: Filter<GetUserGroupsRow>;
  customTokenRender?: (
    options: HidableOption[],
    actions: { removeToken: (value: string) => void },
    users: GetUsersData | undefined,
    userGroups: GetUserGroupsData | undefined,
    showInheritedContributors: boolean
  ) => ReactNode;
  disabledValues?: Set<string>;
  showInheritedContributorsToggle?: boolean;
  disabledOptions?: { userId: string; reason: string }[];
}

export const ControlledGroupAndUserMultiSelect = <T extends FieldValues>({
  includeGroups,
  userFilter,
  groupFilter,
  disabledValues,
  showInheritedContributorsToggle,
  disabledOptions,
  ...props
}: Props<T>) => {
  return (
    <UserSearchPreferences
      showInheritedContributorsToggle={showInheritedContributorsToggle ?? false}
      includeGroups={includeGroups}
      userFilter={userFilter}
      groupFilter={groupFilter}
      disabledValues={disabledValues}
      disabledOptions={disabledOptions}
    >
      {({
        onBlur,
        onChange,
        statusType,
        options,
        userGroups,
        users,
        preferencesButton,
        showInheritedContributors,
      }) => (
        <ControlledMultiselect
          className={styles.root}
          statusType={statusType}
          hideTokens={true}
          options={options}
          renderTokens={true}
          {...props}
          onChange={onChange}
          customTokenRender={
            props.customTokenRender
              ? (...args) => {
                  return props?.customTokenRender?.(
                    ...args,
                    users,
                    userGroups,
                    showInheritedContributors
                  );
                }
              : undefined
          }
          onBlur={onBlur}
          sideControl={preferencesButton}
          previewChangesFormatter={(
            val: [{ UserId?: string; value?: string }] | null | undefined
          ) => {
            if (Array.isArray(val)) {
              return val
                .map((v) => v.UserId ?? v.value)
                .map(
                  (v) =>
                    users?.user.find((u) => u.Id === v)?.FriendlyName ??
                    userGroups?.user_group.find((ug) => ug.Id === v)?.Name
                )
                .join(', ');
            }

            return '-';
          }}
          hasFieldChanged={(
            val:
              | {
                  from: { UserId?: string; value?: string }[];
                  to: { UserId?: string; value?: string }[];
                }
              | null
              | undefined
          ) => {
            if (val === undefined || val === null) {
              return false;
            }
            const from = val.from?.map((v) => v.UserId ?? v.value).sort();
            const to = val.to?.map((v) => v.UserId ?? v.value).sort();

            return !_.isEqual(from, to);
          }}
        />
      )}
    </UserSearchPreferences>
  );
};
